import { ref, readonly } from 'vue'

/**
 * useToast — file globale de notifications éphémères.
 *
 * Singleton volontaire : les toasts sont une preocupation transversale
 * (créés depuis n'importe quel composable, affichés en haut-de-DOM via
 * `PToastContainer`). Le composable retourne les méthodes push/clear et
 * la file readonly.
 *
 * Convention V1 : 4 niveaux (success / error / info / warning), durée
 * auto-dismiss configurable (défaut 4s, sauf error qui reste 8s — l'user
 * doit avoir le temps de lire le message).
 *
 * Cf. spec §13.3 (Notifier port) et §14.2 (PToast).
 */

export type ToastLevel = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
    id: string
    level: ToastLevel
    message: string
    /** Durée avant auto-dismiss en ms. 0 = pas de dismiss auto. */
    duration: number
}

const toasts = ref<Toast[]>([])
const timers = new Map<string, ReturnType<typeof setTimeout>>()

function defaultDuration(level: ToastLevel): number {
    return level === 'error' ? 8000 : 4000
}

function push(level: ToastLevel, message: string, duration?: number): string {
    const id = crypto.randomUUID()
    const finalDuration = duration ?? defaultDuration(level)
    toasts.value.push({ id, level, message, duration: finalDuration })
    if (finalDuration > 0) {
        const timer = setTimeout(() => dismiss(id), finalDuration)
        timers.set(id, timer)
    }
    return id
}

function dismiss(id: string) {
    const index = toasts.value.findIndex((toast) => toast.id === id)
    if (index >= 0) {
        toasts.value.splice(index, 1)
    }
    const timer = timers.get(id)
    if (timer) {
        clearTimeout(timer)
        timers.delete(id)
    }
}

function clear() {
    toasts.value = []
    timers.forEach((timer) => clearTimeout(timer))
    timers.clear()
}

export function useToast() {
    return {
        toasts: readonly(toasts),
        success: (message: string, duration?: number) => push('success', message, duration),
        error: (message: string, duration?: number) => push('error', message, duration),
        info: (message: string, duration?: number) => push('info', message, duration),
        warning: (message: string, duration?: number) => push('warning', message, duration),
        dismiss,
        clear,
    }
}
